/**
 * Asoebi deliveries and zone fees —
 * /planning/:project_id/asoebi/{deliveries,zone-fees}* (kal shape).
 */
import { AsoebiDelivery, AsoebiDeliveryCreate, AsoebiDeliveryUpdate, ZoneFee } from '../types/ui/Asoebi';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

let deliveriesCache: AsoebiDelivery[] = [];
let zoneFeesCache: ZoneFee[] = [];
const DELIVERIES_CHANGE_EVENT = 'ans_asoebi_deliveries_change';
const dispatchChange = () => window.dispatchEvent(new Event(DELIVERIES_CHANGE_EVENT));

export const loadDeliveries = (): AsoebiDelivery[] => deliveriesCache;

export const refreshDeliveriesFromApi = async (archived = false): Promise<AsoebiDelivery[]> => {
  const res = await apiGet<{ deliveries: AsoebiDelivery[] }>(planningPath(`/asoebi/deliveries${archived ? '?archived=true' : ''}`));
  if (!archived) { deliveriesCache = res.deliveries; dispatchChange(); }
  return res.deliveries;
};

export const createDelivery = async (data: AsoebiDeliveryCreate): Promise<AsoebiDelivery> => {
  const created = await apiPost<AsoebiDelivery>(planningPath('/asoebi/deliveries'), data);
  deliveriesCache = [...deliveriesCache, created];
  dispatchChange();
  return created;
};

export const updateDelivery = async (id: string, updates: AsoebiDeliveryUpdate): Promise<AsoebiDelivery> => {
  const updated = await apiPut<AsoebiDelivery>(planningPath(`/asoebi/deliveries/${id}`), updates);
  deliveriesCache = deliveriesCache.map((d) => (d.id === id ? updated : d));
  dispatchChange();
  return updated;
};

export const archiveDelivery = async (id: string): Promise<void> => {
  await apiDelete(planningPath(`/asoebi/deliveries/${id}`));
  deliveriesCache = deliveriesCache.filter((d) => d.id !== id);
  dispatchChange();
};
export const deleteDelivery = archiveDelivery;

export const restoreDelivery = (id: string): Promise<AsoebiDelivery> => apiPost<AsoebiDelivery>(planningPath(`/asoebi/deliveries/${id}/restore`), {});
export const permanentlyDeleteDelivery = (id: string): Promise<void> => apiDelete(planningPath(`/asoebi/deliveries/${id}/permanent`));

export const getDeliveryLines = (id: string) => apiGet<{ lines: unknown[] }>(planningPath(`/asoebi/deliveries/${id}/lines`));
export const attachDeliveryLine = (id: string, order_line_id: string, quantity: number) =>
  apiPost(planningPath(`/asoebi/deliveries/${id}/lines`), { order_line_id, quantity });
export const detachDeliveryLine = (id: string, order_line_id: string) => apiDelete(planningPath(`/asoebi/deliveries/${id}/lines/${order_line_id}`));

export const getAsoebiDeliveryFeeSummary = () => apiGet<{ owed: string; paid: string; currency: string | null }>(planningPath('/asoebi/deliveries/fee-summary'));

// ── Zone fees ──

export const loadZoneFees = (): ZoneFee[] => zoneFeesCache;

export const refreshZoneFeesFromApi = async (): Promise<ZoneFee[]> => {
  const res = await apiGet<{ zone_fees: ZoneFee[] }>(planningPath('/asoebi/zone-fees'));
  zoneFeesCache = res.zone_fees;
  return res.zone_fees;
};

export const createZoneFee = async (zone_key: string, zone_label: string, fee: string): Promise<ZoneFee> => {
  const created = await apiPost<ZoneFee>(planningPath('/asoebi/zone-fees'), { zone_key, zone_label, fee });
  zoneFeesCache = [...zoneFeesCache, created];
  return created;
};

export const updateZoneFee = async (zone_key: string, fee: string): Promise<ZoneFee> => {
  const updated = await apiPut<ZoneFee>(planningPath(`/asoebi/zone-fees/${zone_key}`), { fee });
  zoneFeesCache = zoneFeesCache.map((z) => (z.zone_key === zone_key ? updated : z));
  return updated;
};

export const deleteZoneFee = async (zone_key: string): Promise<void> => {
  await apiDelete(planningPath(`/asoebi/zone-fees/${zone_key}`));
  zoneFeesCache = zoneFeesCache.filter((z) => z.zone_key !== zone_key);
};
